import React, { Suspense, lazy, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute } from "./lib/auth";
import CustomerOnboardingGate from "./components/CustomerOnboardingGate";
import TutorAccessGate from "./components/TutorAccessGate";
import Layout from "./components/Layout";
import { TutorialProvider } from "./components/tutorial/TutorialProvider";
import AppDialogHost from "./components/AppDialogHost";
import AppErrorBoundary from "./components/AppErrorBoundary";
import DashboardHomeRedirect from "./components/DashboardHomeRedirect";
const AbnTester = lazy(() => import("./pages/AbnTester"));
const TutorialController = lazy(() =>
  import("./components/tutorial/TutorialController").then((module) => ({
    default: module.TutorialController,
  }))
);

const Home = lazy(() => import("./pages/Home"));
const FindTutor = lazy(() => import("./pages/FindTutor"));
const TutorProfile = lazy(() => import("./pages/TutorProfile"));
const Pricing = lazy(() => import("./pages/Pricing"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Subjects = lazy(() => import("./pages/Subjects"));
const TutorTiers = lazy(() => import("./pages/TutorTiers"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourcesShowcase = lazy(() => import("./pages/ResourcesShowcase"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Legal = lazy(() => import("./pages/Legal"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const BecomeTutor = lazy(() => import("./pages/BecomeTutor"));
const TutorInterview = lazy(() => import("./pages/TutorInterview"));
const TutorOnboarding = lazy(() => import("./pages/TutorOnboarding"));
const TutorPlatformOrientation = lazy(() => import("./pages/TutorPlatformOrientation"));
const BookingRequest = lazy(() => import("./pages/BookingRequest"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ParentBilling = lazy(() => import("./pages/ParentBilling"));
const Messages = lazy(() => import("./pages/Messages"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const EditTutorProfile = lazy(() => import("./pages/EditTutorProfile"));
const Library = lazy(() => import("./pages/Library"));
const FIND_TUTOR_GUIDE_SESSION_KEY = "austuition.find_tutor_guide_seen";
export const MESSAGES_RETURN_PATH_KEY = "austuition.messages.return_path";

function isTutorBrowseFlowPath(pathname) {
  return pathname === "/find-a-tutor" || pathname.startsWith("/tutors/") || pathname.startsWith("/book/");
}

function isPortalDashboardTabPath(pathname) {
  if (typeof pathname !== "string") return false;
  return (
    pathname.startsWith("/dashboard/student/") ||
    pathname.startsWith("/dashboard/tutor/") ||
    pathname.startsWith("/dashboard/admin/")
  );
}

function LegacyResourceRedirect() {
  return <Navigate to="/resources" replace />;
}

function AppRouter() {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const previousRouteRef = useRef(`${location.pathname}${location.search || ""}${location.hash || ""}`);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    // Keep scroll position when switching tabs inside the same portal/admin shell.
    const preserveDashboardTabScroll =
      isPortalDashboardTabPath(previousPath) && isPortalDashboardTabPath(currentPath);
    if (!preserveDashboardTabScroll) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    const currentRoute = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    const leavingTutorBrowseFlow = isTutorBrowseFlowPath(previousPath) && !isTutorBrowseFlowPath(currentPath);

    if (currentPath === "/messages" && previousPath && previousPath !== "/messages") {
      try {
        window.sessionStorage.setItem(MESSAGES_RETURN_PATH_KEY, previousRouteRef.current || previousPath);
      } catch {
        // Ignore storage issues and fall back to history/dashboard behavior.
      }
    }

    if (leavingTutorBrowseFlow) {
      try {
        window.sessionStorage.removeItem(FIND_TUTOR_GUIDE_SESSION_KEY);
      } catch {
        // Ignore storage errors; modal will still work for current page instance.
      }
    }

    previousPathRef.current = currentPath;
    previousRouteRef.current = currentRoute;
  }, [location.hash, location.pathname, location.search]);
  return (
    <CustomerOnboardingGate>
    <TutorAccessGate>
    <AppErrorBoundary>
      <Routes>
        {/* Shared marketing shell — Navbar/Footer stay mounted across page hops */}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/find-a-tutor" element={<FindTutor />} />
          <Route path="/tutors/:id" element={<TutorProfile />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/subjects" element={<Subjects />} />
          <Route path="/tutor-tiers" element={<TutorTiers />} />
          <Route path="/blog" element={<Navigate to="/" replace />} />
          <Route path="/blog/:slugOrId" element={<Navigate to="/" replace />} />
          <Route path="/resources" element={<ResourcesShowcase />} />
          <Route path="/resources/:id" element={<LegacyResourceRedirect />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/book/:tutorId" element={<ProtectedRoute><BookingRequest /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route element={<Layout showFooter={false} />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/parent-billing/:token" element={<ParentBilling />} />
          <Route path="/parent-billing/:token/success" element={<ParentBilling success />} />
        </Route>

        <Route element={<Layout showNavbar={false} footerMode="compact" />}>
          <Route path="/become-a-tutor" element={<BecomeTutor />} />
          <Route path="/tutor-interview" element={<TutorInterview />} />
          <Route path="/tutor-onboarding/:token" element={<TutorOnboarding />} />
          <Route path="/dev/abn-tester" element={<AbnTester />} />
        </Route>

        <Route element={<Layout footerMode="compact" />}>
          <Route path="/resources/library" element={<ProtectedRoute roles={["student", "parent"]}><Resources /></ProtectedRoute>} />
          <Route path="/onboarding/tutor/platform" element={<ProtectedRoute roles={["tutor"]}><TutorPlatformOrientation /></ProtectedRoute>} />
          {process.env.NODE_ENV === "development" ? (
            <Route path="/dev/tutor-academy" element={<TutorPlatformOrientation preview />} />
          ) : null}
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute roles={["student", "parent", "tutor", "admin"]}><Library /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardHomeRedirect /></ProtectedRoute>} />
          <Route path="/dashboard/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/dashboard/edit-profile" element={<ProtectedRoute roles={["tutor", "admin"]}><EditTutorProfile /></ProtectedRoute>} />
          <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Route>

        <Route path="/onboarding/parent" element={<ProtectedRoute roles={["parent"]}><DashboardHomeRedirect /></ProtectedRoute>} />
        <Route path="/onboarding/student" element={<ProtectedRoute roles={["student"]}><DashboardHomeRedirect /></ProtectedRoute>} />
      </Routes>
    </AppErrorBoundary>
    </TutorAccessGate>
    </CustomerOnboardingGate>
  );
}

function NotFound() {
  return (
    <div className="container-aus py-24 text-center">
      <div className="tiny-caps mb-2">404</div>
      <h1 className="font-heading text-5xl font-bold">Page not found</h1>
      <p className="text-aus-onyx/60 mt-3">Let's get you back on track.</p>
      <Link to="/" className="btn-primary inline-flex mt-6 text-sm">Go home</Link>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TutorialProvider>
            <AppDialogHost />
            <AppRouter />
            <AppErrorBoundary>
              <Suspense fallback={null}>
                <TutorialController />
              </Suspense>
            </AppErrorBoundary>
          </TutorialProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
